import React, { useState } from "react";
import { Leaf, TrendingUp, CheckCircle2, ArrowRight, FileText, Lock, AlertTriangle, Eye } from "lucide-react";
import { Helmet } from "react-helmet";
import app from "./firebase";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";

const db = getFirestore(app);

export default function InvestorLandingPage() {
  const [formData, setFormData] = useState({
    investmentRange: "₦500,000 – ₦2,000,000",
    cropInterest: "Cocoa Processing & Export",
    horizon: "12 Months",
    fullName: "",
    email: "",
    phone: "",
    riskAcknowledged: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.riskAcknowledged) {
      alert("Please review and acknowledge the agricultural risk disclosure before submitting.");
      return;
    }
    setLoading(true);
    try {
      await addDoc(collection(db, "investor_leads"), {
        ...formData,
        createdAt: serverTimestamp(),
        source: "investor_landing"
      });
    } catch (err) {
      console.warn("Firestore save fallback:", err);
    }
    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div style={{ fontFamily: "var(--font-sans, system-ui, sans-serif)", color: "#1e293b", background: "#f8fafc", minHeight: "100vh" }}>
      <Helmet>
        <title>Agricultural Investment Opportunities & Briefings — Agrivestia</title>
        <meta name="description" content="Review verified farm investment opportunities, crop aggregation structures, and agricultural trade financing with transparent terms and risk disclosures." />
        <meta name="keywords" content="farm investment opportunities Nigeria, invest in agriculture Africa, agri tech investment, crop financing, transparent farm returns" />
      </Helmet>

      {/* Header */}
      <header style={{ background: "#ffffff", borderBottom: "1px solid #e2e8f0", padding: "16px 24px", position: "sticky", top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: "8px", textDecoration: "none" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Leaf size={20} color="white" />
            </div>
            <span style={{ fontSize: "20px", fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>AGRIVESTIA <span style={{ color: "#16a34a", fontSize: "14px", fontWeight: 700 }}>INVESTORS</span></span>
          </a>
          <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
            <a href="/contact" style={{ color: "#475569", textDecoration: "none", fontSize: "14px", fontWeight: 600 }}>Talk to Advisory</a>
            <a href="#investor-form" style={{ background: "#16a34a", color: "#ffffff", padding: "10px 20px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, fontSize: "14px" }}>Receive Farm Opportunities</a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section style={{ background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)", color: "#ffffff", padding: "60px 24px 80px" }}>
        <div style={{ maxWidth: "1200px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "48px", alignItems: "center" }}>
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)", padding: "6px 14px", borderRadius: "20px", fontSize: "13px", fontWeight: 700, color: "#4ade80", marginBottom: "20px" }}>
              <TrendingUp size={16} /> Transparent Farm Capital & Trade Financing
            </div>
            <h1 style={{ fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 900, lineHeight: 1.15, marginBottom: "20px", letterSpacing: "-0.02em" }}>
              Review Farm Opportunities with Transparent Terms & Clear Risk Disclosures
            </h1>
            <p style={{ fontSize: "18px", color: "#cbd5e1", lineHeight: 1.6, marginBottom: "28px" }}>
              Partner with Agrivestia to fund structured farm operations, processing infrastructure, and export commodity trade backed by satellite crop monitoring and escrow governance.
            </p>

            {/* Risk Disclosure Box */}
            <div style={{ background: "rgba(245, 158, 11, 0.1)", border: "1px solid rgba(245, 158, 11, 0.3)", borderRadius: "12px", padding: "16px", marginBottom: "28px", color: "#fef3c7", fontSize: "14px", lineHeight: 1.5 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, color: "#fbbf24", marginBottom: "4px" }}>
                <AlertTriangle size={18} /> Important Investor Notice & Risk Disclosure
              </div>
              Agricultural investments carry operational, weather, and commodity price risks. Agrivestia provides transparent terms and insured escrow structures, but never guarantees arbitrary returns. Past cycle performance is not an indicator of guaranteed future yield.
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "20px", fontSize: "14px", color: "#94a3b8" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#22c55e" /> Insured Warehouses & Crops</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#22c55e" /> Quarterly Agronomic Audit Reports</div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={18} color="#22c55e" /> Escrow Allocation Control</div>
            </div>
          </div>

          {/* Lead Form */}
          <div id="investor-form" style={{ background: "#ffffff", borderRadius: "16px", padding: "32px", color: "#0f172a", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.3)" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "32px 16px" }}>
                <div style={{ width: "64px", height: "64px", borderRadius: "50%", background: "#dcfce7", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                  <CheckCircle2 size={36} />
                </div>
                <h3 style={{ fontSize: "24px", fontWeight: 800, marginBottom: "12px" }}>Opportunity Briefing Sent!</h3>
                <p style={{ color: "#475569", fontSize: "15px", lineHeight: 1.6, marginBottom: "24px" }}>
                  Thank you, <strong>{formData.fullName}</strong>. We have dispatched our current agricultural opportunity memorandum and risk documentation to <strong>{formData.email}</strong>. Our investment desk will be in touch shortly.
                </p>
                <a href="/" style={{ background: "#16a34a", color: "#ffffff", padding: "12px 24px", borderRadius: "8px", textDecoration: "none", fontWeight: 700, display: "inline-block" }}>
                  Return to Homepage &rarr;
                </a>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <h2 style={{ fontSize: "22px", fontWeight: 800, color: "#0f172a", marginBottom: "4px" }}>Receive Current Farm Opportunities</h2>
                <p style={{ fontSize: "13px", color: "#64748b", margin: "-8px 0 8px" }}>Get current farm project terms, capital allocation, and risk disclosure briefing.</p>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Preferred Investment Range *</label>
                  <select name="investmentRange" value={formData.investmentRange} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", background: "#ffffff" }}>
                    <option>₦500,000 – ₦2,000,000</option>
                    <option>₦2,000,000 – ₦10,000,000</option>
                    <option>₦10,000,000 – ₦50,000,000+</option>
                    <option>Institutional / Corporate Capital</option>
                  </select>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Crop / Sector Interest</label>
                    <select name="cropInterest" value={formData.cropInterest} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", background: "#ffffff" }}>
                      <option>Cocoa Processing & Export</option>
                      <option>Cashew Aggregation & Processing</option>
                      <option>Sesame Seed Export Trade</option>
                      <option>Grain Storage & Trade Finance</option>
                      <option>Diversified Agri-Portfolio</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Investment Horizon</label>
                    <select name="horizon" value={formData.horizon} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px", background: "#ffffff" }}>
                      <option>6 Months (Trade Finance)</option>
                      <option>12 Months (Crop Cycle)</option>
                      <option>24+ Months (Infrastructure)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Your Full Name *</label>
                  <input type="text" name="fullName" required placeholder="Adesola Okafor" value={formData.fullName} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>Email Address *</label>
                    <input type="email" name="email" required placeholder="investor@example.com" value={formData.email} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#334155", marginBottom: "4px" }}>WhatsApp / Phone *</label>
                    <input type="tel" name="phone" required placeholder="+234 803 123 4567" value={formData.phone} onChange={handleChange} style={{ width: "100%", padding: "10px", border: "1px solid #cbd5e1", borderRadius: "8px", fontSize: "14px" }} />
                  </div>
                </div>

                <div style={{ background: "#fffbebf0", border: "1px solid #fef08a", padding: "12px", borderRadius: "8px", display: "flex", gap: "10px", alignItems: "flex-start", marginTop: "4px" }}>
                  <input type="checkbox" id="riskCheck" name="riskAcknowledged" checked={formData.riskAcknowledged} onChange={handleChange} style={{ marginTop: "3px", width: "16px", height: "16px", cursor: "pointer" }} />
                  <label htmlFor="riskCheck" style={{ fontSize: "12px", color: "#854d0e", cursor: "pointer", lineHeight: 1.4 }}>
                    <strong>Mandatory Risk Acknowledgment:</strong> I understand that agricultural investments carry inherent market, seasonal, and operational risks and agree to review the full disclosure documentation.
                  </label>
                </div>

                <button type="submit" disabled={loading} style={{ background: "#16a34a", color: "#ffffff", border: "none", padding: "14px", borderRadius: "8px", fontWeight: 800, fontSize: "16px", cursor: "pointer", marginTop: "8px", display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
                  {loading ? "Preparing Briefing..." : "Receive Opportunity Briefing"} <ArrowRight size={18} />
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Proof & Due Diligence */}
      <section style={{ padding: "80px 24px", maxWidth: "1200px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "48px" }}>
          <h2 style={{ fontSize: "32px", fontWeight: 900, color: "#0f172a", marginBottom: "12px" }}>Agrivestia Due Diligence & Risk Mitigation Framework</h2>
          <p style={{ color: "#64748b", fontSize: "16px" }}>How we protect capital, track field operations, and enforce escrow compliance.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px" }}>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "28px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
              <Eye size={24} />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Satellite & Agronomic Monitoring</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Field operations are monitored using remote sensing, periodic drone imagery, and third-party agronomist field visits.</p>
          </div>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "28px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
              <Lock size={24} />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Escrow Controlled Capital</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Investor capital is disbursed in milestone-based tranches (seedlings, land prep, harvesting, buyer delivery) to prevent misuse.</p>
          </div>
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "28px" }}>
            <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: "#f0fdf4", color: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
              <FileText size={24} />
            </div>
            <h3 style={{ fontSize: "18px", fontWeight: 800, color: "#0f172a", marginBottom: "8px" }}>Offtake Contracts</h3>
            <p style={{ color: "#64748b", fontSize: "14px", lineHeight: 1.6 }}>Crop production is backed by pre-negotiated off-take agreements with verified buyers and processors before planting commences.</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: "#052e16", color: "#94a3b8", padding: "40px 24px", textAlign: "center", fontSize: "14px" }}>
        <p>&copy; 2026 Agrivestia Ag-Finance. All rights reserved. | <a href="/" style={{ color: "#4ade80", textDecoration: "none" }}>Homepage</a> | <a href="/privacy-policy" style={{ color: "#4ade80", textDecoration: "none" }}>Privacy Policy</a> | <a href="/terms-of-service" style={{ color: "#4ade80", textDecoration: "none" }}>Terms of Service</a></p>
      </footer>
    </div>
  );
}
